import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { Features } from '@app/core/user-permissions/user-permissions.model';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';

import { AbstractControl, UntypedFormBuilder } from '@angular/forms';
import { NewDirectCustomQuote } from '@app/shared/services/direct-quotes/direct-quotes.service';
import { ManualBooking } from '@app/shared/services/manual-bookings/manual-bookings.service';
import { OptimizelyService } from '@app/shared/services/optimizely/optimizely.service';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { dedupeArray } from '@app/shared/utils';
import { requireBothCICOValidator } from '@app/shared/validators';
import { eachDay, format } from 'date-fns';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CalendarSource, CalendarSourceType } from '../../data/calendar-source.interface';
import { DynamicPricingService } from '@app/features/pricing-and-availability';

const AVAILABLE_LABELS = {
  trueSelected: 'Available',
  falseSelected: 'Blocked',
  true: 'Open night',
  false: 'Block',
};

const inverseOfNullable = (value: boolean | null | string) => {
  if (value === null) {
    return null;
  }

  return !value;
};
export interface SidebarPricingSaveEvent {
  dates;
  price;
  availability;
  los;
  maxStay;
  note;
  daysOfTheWeek?;
  closedForCheckin?;
  closedForCheckout?;
}
@Component({
  standalone: false,
  selector: 'sbnb-sidebar-pricing',
  templateUrl: './sidebar-pricing.component.html',
  styleUrls: ['./sidebar-pricing.component.scss'],
})
export class SidebarPricingComponent implements OnInit, OnChanges, OnDestroy {
  @Input('property') clickedProperty: any;
  @Input() multipleProperties: any;

  @Input('dates') hoveredDates: any[];
  @Input('loading') saving: boolean;
  @Input() readonly = false;
  @Input() readonlyTooltip?: string;

  @Output() close: EventEmitter<boolean> = new EventEmitter();
  @Output() save: EventEmitter<SidebarPricingSaveEvent> = new EventEmitter();
  @Output() manualBookingSaved: EventEmitter<{
    created?: ManualBooking;
    updated: ManualBooking;
    cancelled: ManualBooking;
  }> = new EventEmitter();
  @Output() customQuoteCreated: EventEmitter<{
    quote: NewDirectCustomQuote;
  }> = new EventEmitter();
  @Output() resetToDynamicPricing: EventEmitter<void> = new EventEmitter();
  user = this.authService.getUserDetails();

  placeholderPrices: number[];
  placeholderLengthOfStays: number[];
  placeholderMaxLengthOfStays: number[];
  public availabilityForm = this.fb.group(
    {
      availability: [null],
      availabilityForCheckin: [null],
      availabilityForCheckout: [null],
      note: [''],
    },
    { validators: requireBothCICOValidator }
  );
  placeholderAvailability: boolean;

  desiredPrice: number;
  desiredLengthOfStay: number;
  desiredMaxLengthOfStay: number;
  notePlaceholder = '';

  failures: any[];

  failureTypes = {
    price: 'Price',
    availability: 'Availability',
    min_stay: 'Minimum length of stay',
  };

  multiDayFailure: boolean;
  sources: any;

  new_failures: any;
  multiDayNewFailure: boolean;

  perms$ = this.userPermissionsService;
  Features = Features;

  sourceData: CalendarSource[] = [];
  isManualPrice = false;

  shouldShowDaySelection = false;

  maxNightsEnabled = false;

  public daysToApplyChangesOnSave = [0, 1, 2, 3, 4, 5, 6]; // all are selected by default
  public availableLabels = AVAILABLE_LABELS;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  public sourceTypeEnum = CalendarSourceType;
  public startOfWeekPreference = this.authService.getStartOfWeekPreference();
  public restrictedCheckin = false;
  public restrictedCheckout = false;
  public $dynamicPricingOn = this.dynamicPricingService.$dynamicPricingOn;

  constructor(
    private readonly authService: AuthenticationService,
    private userPermissionsService: UserPermissionsService,
    private segmentIoService: SegmentIoService,
    private fb: UntypedFormBuilder,
    private optimizelyService: OptimizelyService,
    private dynamicPricingService: DynamicPricingService
  ) {}

  ngOnInit(): void {
    this.availabilityForm.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe((status) => {
      if (!(status === 'INVALID' && this.availabilityForm?.errors?.['requireBothCICO'])) {
        return;
      }
      this.segmentIoService.track(SegmentEvent.CalendarCICOSetBothError, {});
    });

    this.availabilityForm
      .get('availability')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        this.trackEventCICOVisibility(value);
      });

    this.checkForMaxNightsEnabled();
  }

  public ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  checkForDaySelection() {
    this.shouldShowDaySelection = this.hoveredDates && this.hoveredDates.length >= 7 ? true : false;
    this.shouldShowDaySelection ? this.segmentIoService.track(SegmentEvent.StartedCalendarBulkEdit, {}) : null;
  }

  ngOnChanges() {
    this.setFormStatus();
    this.checkForDaySelection();
    // Determine price range and availabilities for placeholders
    this.calculatePlaceholderValues();
    this.calculateFailures();
    // Showing source information for pricing / availability / min LOS
    this.calculateSourceInformation();
  }

  private setFormStatus() {
    this.saving || this.readonly ? this.availabilityForm.disable() : this.availabilityForm.enable();
  }

  private calculateFailures() {
    if (this.hoveredDates.length === 1) {
      const singleProperty = this.clickedProperty[format(this.hoveredDates[0], 'YYYY-MM-DD')];
      this.failures = singleProperty?.failures;
      this.new_failures = singleProperty?.new_failures;
      this.sources = singleProperty?.sources;
      return;
    }
    // if any day contains failures, we set the multiDayFailure boolean
    this.hoveredDates.forEach((date) => {
      const property = this.clickedProperty[format(date, 'YYYY-MM-DD')];
      if (property?.failures) {
        this.multiDayFailure = true;
      }

      if (property?.new_failures) {
        this.multiDayNewFailure = true;
      }
    });
  }

  private calculatePlaceholderValues() {
    this.resetAvailabilityControls({
      availability: null,
      availabilityForCheckin: null,
      availabilityForCheckout: null,
    });
    const prices = [];
    const lengthOfStays = [];
    const maxLengthOfStays = [];
    const availabilities = [];
    const notes = [];
    // Note that we get CLOSED from the server but show the user the inverse
    const closedForCheckin = [];
    const closedForCheckout = [];
    this.restrictedCheckin = false;
    this.restrictedCheckout = false;

    this.hoveredDates.forEach((date) => {
      const clickedProperty = this.clickedProperty[format(date, 'YYYY-MM-DD')];
      prices.push(clickedProperty?.price?.amount);
      availabilities.push(clickedProperty?.available);
      if (clickedProperty?.note?.length) {
        notes.push(clickedProperty?.note);
      }
      lengthOfStays.push(clickedProperty?.minimum_length_of_stay);
      maxLengthOfStays.push(clickedProperty?.maximum_length_of_stay);

      if (typeof clickedProperty?.closed_for_checkin !== 'undefined') {
        closedForCheckin.push(clickedProperty?.closed_for_checkin);
        closedForCheckout.push(clickedProperty?.closed_for_checkout);
      }
      if (clickedProperty?.restrictions?.can_edit_check_in === false) {
        this.restrictedCheckin = true;
      }
      if (clickedProperty?.restrictions?.can_edit_check_out === false) {
        this.restrictedCheckout = true;
      }
    });

    this.placeholderPrices = dedupeArray(prices.sort((a, b) => a - b));

    // Wipe out the placeholder if the currency of any day does not match the properties currency
    this.wipePlaceholderPriceIfCurrencyMismatch();

    this.placeholderLengthOfStays = dedupeArray(
      lengthOfStays
        .map((a) => a ?? null)
        .sort((a, b) => {
          if (a == null && b == null) return 0;
          if (a == null) return -1;
          if (b == null) return 1;
          return a - b;
        })
    );

    this.placeholderMaxLengthOfStays = dedupeArray(
      maxLengthOfStays
        .map((a) => a ?? null)
        .sort((a, b) => {
          if (a == null && b == null) return 0;
          if (a == null) return -1;
          if (b == null) return 1;
          return a - b;
        })
    );

    const availabilityUnique = dedupeArray(availabilities);
    const newAvailability = availabilityUnique.length === 1 ? availabilityUnique[0] : null;
    this.availabilityForm.get('availability').setValue(newAvailability);
    this.placeholderAvailability = newAvailability;
    this.trackEventCICOVisibility(newAvailability);

    const notesUnique = dedupeArray(notes);
    this.setNotePlaceholder(notesUnique);
    this.availabilityForm.get('note').setValue(notesUnique?.length === 1 ? notesUnique[0] : '');

    this.setAvailabilityControls(this.availabilityForm.get('availabilityForCheckin'), closedForCheckin);
    this.setAvailabilityControls(this.availabilityForm.get('availabilityForCheckout'), closedForCheckout);

    // CICO must be sent as a pair. If one of them is disabled AND the other one is null because the user has selelcted
    // a date range where the values are mixed, we must disable both to stop them from sending a non-sensical request
    // to the server. This is a tricky UI edge case because of the way the user can select any range and the server
    // requires very specific values.
    if (this.restrictedCheckin) {
      this.availabilityForm.get('availabilityForCheckin').disable();
      if (this.availabilityForm.get('availabilityForCheckin').value === null) {
        this.availabilityForm.get('availabilityForCheckout').disable();
      }
    }
    if (this.restrictedCheckout) {
      this.availabilityForm.get('availabilityForCheckout').disable();
      if (this.availabilityForm.get('availabilityForCheckout').value === null) {
        this.availabilityForm.get('availabilityForCheckin').disable();
      }
    }
  }

  public getPlaceholderLengthOfStays(placeholderLengthOfStays: number[]) {
    if (!placeholderLengthOfStays?.length) return '';

    const calculate = (placeholderLengthOfStays: number[]) => {
      if (placeholderLengthOfStays?.length > 1) {
        return placeholderLengthOfStays[0] + '-' + placeholderLengthOfStays[placeholderLengthOfStays.length - 1];
      } else if (placeholderLengthOfStays) {
        return placeholderLengthOfStays[0];
      } else {
        return '';
      }
    };

    if (placeholderLengthOfStays?.length > 1 && placeholderLengthOfStays[0] === null) {
      return 'None, ' + calculate(placeholderLengthOfStays.slice(1));
    }

    return calculate(placeholderLengthOfStays);
  }

  public getPlaceholderMaxLengthOfStays(placeholderMaxLengthOfStays: number[]) {
    if (!placeholderMaxLengthOfStays?.length) return '';

    const calculate = (placeholderMaxLengthOfStays: number[]) => {
      if (placeholderMaxLengthOfStays?.length > 1) {
        return (
          placeholderMaxLengthOfStays[0] + '-' + placeholderMaxLengthOfStays[placeholderMaxLengthOfStays.length - 1]
        );
      } else if (placeholderMaxLengthOfStays) {
        return placeholderMaxLengthOfStays[0];
      } else {
        return '';
      }
    };

    if (placeholderMaxLengthOfStays?.length > 1 && placeholderMaxLengthOfStays[0] === null) {
      return 'None, ' + calculate(placeholderMaxLengthOfStays.slice(1));
    }

    return calculate(placeholderMaxLengthOfStays);
  }

  private setNotePlaceholder(notes: string[]) {
    notes?.length > 1 ? 'You have added multiple notes in this date range.' : 'Add a note';
  }

  private calculateSourceInformation() {
    // If you selected multiple properties, we just give you a flat out multiple
    if (this.multipleProperties?.length > 1) {
      this.sourceData = Object.values(CalendarSourceType).map((type) => ({
        type,
        source: 'multiple',
        date: null,
      }));

      return;
    }

    // If you're only running 1 day, we can easily return the source data as it is
    if (this.hoveredDates?.length === 1) {
      const day = this.clickedProperty[format(this.hoveredDates[0], 'YYYY-MM-DD')];

      if (!day) {
        this.sourceData = [];
        return;
      }
      this.isManualPrice = day.sources.some(
        (source) => source.type === CalendarSourceType.Price && source.source === 'manual'
      );
      this.sourceData = day.sources;
    }
    // multiple days, we need to concat some of the data and check for matches/differences
    else {
      this.sourceData = [];

      const transformedDates = this.hoveredDates.map((date) => format(date, 'YYYY-MM-DD'));

      // Find the entries for the relevant selected days
      const relevantDays = Object.keys(this.clickedProperty)
        .filter((key) => transformedDates.includes(key))
        .reduce((obj, key) => {
          obj[key] = this.clickedProperty[key];
          return obj;
        }, {});

      Object.values(CalendarSourceType).forEach((sourceType) => {
        this.calculateSource(relevantDays, sourceType);
      });
    }
  }

  private calculateSource(relevantDays: any, sourceType: CalendarSource['type']) {
    if (sourceType === CalendarSourceType.Price) {
      this.isManualPrice = false;
    }
    const data = [];

    // Find all the pricing entries on the given days
    Object.values(relevantDays).forEach((day) => {
      if ((day as any).sources.find((source) => source.type === sourceType)) {
        data.push((day as any).sources.find((source) => source.type === sourceType));
      }
    });

    // Do all pricing entries have the same source?
    const areAllPricingSourcesIdentical = data.length > 0 && data.every((val) => val?.source === data[0]?.source);

    if (data.length === 0) {
      this.sourceData.push({
        type: sourceType,
        source: 'none',
        date: null,
      });

      return;
    }

    if (areAllPricingSourcesIdentical && data.length > 0) {
      this.sourceData.push({
        type: sourceType,
        source: data[0].source,
        date: null,
      });

      if (sourceType === CalendarSourceType.Price) {
        this.isManualPrice = data[0].source === 'manual';
      }
    } else {
      this.sourceData.push({
        type: sourceType,
        source: 'multiple',
        date: null,
      });
    }
  }

  private wipePlaceholderPriceIfCurrencyMismatch() {
    const propertyCurrency = this.clickedProperty.currency;
    const days = this.hoveredDates.map((date) => {
      return format(date, 'YYYY-MM-DD');
    });

    let hasMismatch = false;

    days.forEach((day) => {
      if (this.clickedProperty[day]?.price?.currency !== propertyCurrency) {
        hasMismatch = true;
      }
    });

    if (hasMismatch) {
      this.placeholderPrices = [];
    }
  }

  sidebarDatesChanged(event: Date[]) {
    this.hoveredDates = this.fillDates(event[0], event[1]);

    this.checkForDaySelection();
  }

  fillDates(startDate: any, endDate: any): any[] {
    return eachDay(startDate, endDate);
  }

  manualBookingAdded(event: { created?: ManualBooking; updated: ManualBooking; cancelled: ManualBooking }) {
    this.manualBookingSaved.emit(event);
  }

  triggerSave() {
    if (this.readonly) {
      return;
    }
    const {
      availability,
      availabilityForCheckin: closedForCheckin,
      availabilityForCheckout: closedForCheckout,
      note,
    } = this.getAvailabililityFormValuesForSave();

    const data = {
      dates: this.hoveredDates,
      price: this.desiredPrice,
      los: this.desiredLengthOfStay,
      maxStay: this.desiredMaxLengthOfStay,
      note,
      availability,
      closedForCheckin,
      closedForCheckout,
    };
    if (this.shouldShowDaySelection) {
      data['daysOfTheWeek'] = this.daysToApplyChangesOnSave;
    }

    this.resetAvailabilityControls(this.availabilityForm.value);
    this.save.emit(data);
  }

  triggerClose() {
    this.close.emit(true);
  }

  updateDaySelection(event: number[]) {
    if (!event) {
      return;
    }

    this.segmentIoService.track(SegmentEvent.ClickedCalendarBulkEditDay, {});
    this.daysToApplyChangesOnSave = [...event];
  }

  onCustomQuoteCreated(event) {
    this.customQuoteCreated.emit(event);
  }

  public setToggleControl(control: AbstractControl, value: boolean) {
    control.markAsTouched();
    control.markAsDirty();
    control.setValue(value);
  }

  public restrictedCICOTooltip(field: string) {
    if (
      this.availabilityForm.get('availabilityForCheckin').disabled &&
      this.availabilityForm.get('availabilityForCheckout').disabled &&
      this.availabilityForm.get('availability').value === false
    ) {
      return 'Editing of this setting is not allowed on blocked dates.';
    }

    if (this.availabilityForm.get(field).disabled) {
      return 'Editing of this setting is not allowed due to a reservation.';
    }

    return null;
  }

  private setAvailabilityControls(control: AbstractControl, values: any[]) {
    if (!control.dirty && !this.saving) {
      const uniq = dedupeArray(values);

      if (uniq.length === 1) {
        // show the user the inverse
        control.setValue(inverseOfNullable(uniq[0]));
      } else {
        control.setValue(null);
      }
    }
  }

  private resetAvailabilityControls(values) {
    this.availabilityForm.reset(values);
    this.availabilityForm.markAsPristine();
  }

  // Only send values to the server if the user has made a change (dirty),
  // null values will be stripped out in the service
  private getAvailabililityFormValuesForSave() {
    const CICODirty =
      this.availabilityForm.get('availabilityForCheckin').dirty ||
      this.availabilityForm.get('availabilityForCheckout').dirty;
    const availabilityDirty = this.availabilityForm.get('availability').dirty;
    const noteDirty = this.availabilityForm.get('note').dirty;
    const { availability, availabilityForCheckin, availabilityForCheckout } = this.availabilityForm.value;

    return {
      availability: availabilityDirty ? availability : null,
      // The server expects that neither CI or CO are set OR that BOTH are set
      // The server also expects the inverse of what the user selected
      availabilityForCheckin: CICODirty ? !availabilityForCheckin : null,
      availabilityForCheckout: CICODirty ? !availabilityForCheckout : null,
      note: noteDirty ? this.availabilityForm.get('note').value : null,
    };
  }

  /**
   * Using this event to trigger an intercom post to help users understand CICO functionality.
   * Only shown to users the first time the event is fired.
   */
  private trackEventCICOVisibility(value: boolean) {
    if (value !== true) {
      return;
    }
    this.segmentIoService.track(SegmentEvent.CalendarCICOAvailability, {});
  }

  checkForMaxNightsEnabled() {
    this.optimizelyService.calendarMaxNightsEnabled$.subscribe((flag) => {
      this.maxNightsEnabled = flag;
    });
  }

  public emitResetToDynamicPricing() {
    this.resetToDynamicPricing.emit();
  }
}
